import { Injectable, NgZone, signal } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { fromEvent, merge, Subscription, timer } from 'rxjs';
import { filter } from 'rxjs/operators';
import { ASMEApplicationStateService } from '../../features/clients/asme/services/asme-applicationstate.service';
import { IEEEApplicationStateService } from '../../features/clients/ieee/services/ieee-applicationstate.service';
import { ApplicationTypeService } from './appplication-type-service';
import { PoMailingAddressService } from './PoMailingAddressService';

@Injectable({ providedIn: 'root' })
export class InactivityTimeoutService {
  readonly warningVisible = signal(false);
  readonly remainingSeconds = signal(600);

  private readonly warningAfterMs = 5 * 60 * 1000;
  private readonly sessionTimeoutMs = 15 * 60 * 1000;
  private activitySubscription?: Subscription;
  private warningTimerSubscription?: Subscription;
  private warningTimeoutSubscription?: Subscription;
  private currentClient: 'asme' | 'ieee' | null = null;

  constructor(
    private router: Router,
    private zone: NgZone,
    private asmeState: ASMEApplicationStateService,
    private ieeeState: IEEEApplicationStateService,
    private applicationTypeService: ApplicationTypeService,
    private poMailingAddressService: PoMailingAddressService
  ) {
    this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe(() => this.syncWithRoute());

    this.syncWithRoute();
  }

  continueApplication(): void {
    this.warningVisible.set(false);
    this.remainingSeconds.set(this.warningSeconds);
    this.startSession();
  }

  private get warningSeconds(): number {
    return (this.sessionTimeoutMs - this.warningAfterMs) / 1000;
  }

  private syncWithRoute(): void {
    const client = this.isSubmittedPage() ? null : this.getCurrentClient();
    if (client === this.currentClient) {
      if (client) {
        this.startSession();
      }
      return;
    }

    this.currentClient = client;
    this.stopTimers();
    this.warningVisible.set(false);
    this.remainingSeconds.set(this.warningSeconds);

    if (client) {
      this.startSession();
    }
  }

  private startSession(): void {
    this.stopTimers();
    this.warningVisible.set(false);
    this.remainingSeconds.set(this.warningSeconds);

    const activityEvents = merge(
      fromEvent(window, 'click'),
      fromEvent(window, 'keydown'),
      fromEvent(window, 'mousemove'),
      fromEvent(window, 'scroll'),
      fromEvent(window, 'touchstart')
    );

    this.activitySubscription = activityEvents.subscribe(() => {
      if (!this.warningVisible()) {
        this.startSession();
      }
    });

    this.warningTimeoutSubscription = timer(this.warningAfterMs).subscribe(() => {
      this.zone.run(() => this.showWarning());
    });
  }

  private showWarning(): void {
    this.activitySubscription?.unsubscribe();
    this.warningVisible.set(true);
    this.remainingSeconds.set(this.warningSeconds);

    this.warningTimerSubscription = timer(1000, 1000).subscribe(() => {
      this.zone.run(() => {
        const seconds = this.remainingSeconds() - 1;
        this.remainingSeconds.set(Math.max(seconds, 0));
        if (seconds <= 0) {
          this.expireSession();
        }
      });
    });
  }

  private expireSession(): void {
    const client = this.currentClient;
    this.stopTimers();
    this.warningVisible.set(false);

    this.asmeState.clearApplicationSession();
    this.ieeeState.clearApplicationSession();
    this.poMailingAddressService.clear();
    localStorage.removeItem('token');
    this.clearPageStores();

    if (client) {
      const welcomeUrl = this.router.serializeUrl(
        this.router.createUrlTree(['/amba', client, 'welcome'])
      );
      void this.router.navigateByUrl(welcomeUrl).then(() => {
        // Recreate singleton form services so cleared page data cannot survive in memory.
        window.location.reload();
      });
    }
  }

  private clearPageStores(): void {
    const preservedKeys = new Set(['asme_application_data', 'ieee_application_data']);

    try {
      for (let index = sessionStorage.length - 1; index >= 0; index--) {
        const key = sessionStorage.key(index);
        if (key && !preservedKeys.has(key)) {
          sessionStorage.removeItem(key);
        }
      }
    } catch (error) {
      console.warn('Unable to clear page session storage.', error);
    }
  }

  private stopTimers(): void {
    this.activitySubscription?.unsubscribe();
    this.warningTimeoutSubscription?.unsubscribe();
    this.warningTimerSubscription?.unsubscribe();
    this.activitySubscription = undefined;
    this.warningTimeoutSubscription = undefined;
    this.warningTimerSubscription = undefined;
  }

  private getCurrentClient(): 'asme' | 'ieee' | null {
    const appType = this.applicationTypeService.getSnapshot();
    return appType === 'asme' || appType === 'ieee' ? appType : null;
  }

  private isSubmittedPage(): boolean {
    let route = this.router.routerState.snapshot.root;
    while (route) {
      if (route.routeConfig?.path === 'thankyou') {
        return true;
      }
      const child = route.firstChild;
      if (!child) {
        return false;
      }
      route = child;
    }
    return false;
  }
}